import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Typography
} from '@material-ui/core';
import { canOverwriteView, canDeleteView } from '../util/viewListModel';

/**
 * ViewModal - Modal dialog for Add/Save/Edit/Delete view operations
 *
 * @param {string} mode - 'add', 'save', 'edit', or 'delete'
 * @param {boolean} open - Whether modal is open
 * @param {function} onClose - Close handler
 * @param {function} onConfirm - Confirm handler (receives viewData)
 * @param {object} currentView - Current view object (for save/edit/delete mode)
 * @param {array} allViews - All views (to check for default conflicts)
 */
const ViewModal = ({ mode, open, onClose, onConfirm, currentView = null, allViews = [], viewRole = null, initialName = '', isAdmin = false, currentUserId = null, crossServer = false }) => {
  const [viewName, setViewName] = useState('');
  const [isGlobal, setIsGlobal] = useState(false);
  const [isDefault, setIsDefault] = useState(false);
  const [isDividia, setIsDividia] = useState(false);
  const [nameError, setNameError] = useState('');
  // Unified Save (#86bb5dx62, David 2026-07-30): one "Save" replaces the old Save+New.
  // saveTarget = 'overwrite' (update the current view) | 'new' (save as a new view).
  const [saveTarget, setSaveTarget] = useState('new');

  // Permission context (#86bb7phhh, view-permission-model.md): admin = the ADMIN right
  // (state.user.fAdmin) threaded from ViewList; currentUserId = the logged-in user id.
  const ctx = { isAdmin: !!isAdmin, currentUserId };

  // Save-As target gates (view-permission-model §B): Global = admin only; Default = admin AND
  // single-system (a cross-server layout can never be a Default). Dividia-internal stays
  // master-only (cloud bRole=1) — it's a property OF a global, orthogonal to the view-type model.
  const canGlobal = !!isAdmin;
  const canDefaultTarget = !!isAdmin && !crossServer;
  const canDividia = Number(viewRole) === 1;

  // Overwrite (save-over) an existing view — the ONE shared gate (canOverwriteView) so the modal
  // and ViewList's quick-save can't diverge: Smart never; Default/Global admin only; User owner
  // only. A view the user can't overwrite forces Save-As-New.
  const canOverwrite = canOverwriteView(currentView, ctx);

  // Case-insensitive duplicate-name check against the current view list (#86bb5dx62 — David:
  // the old check was case-SENSITIVE). `exclude` skips the view being overwritten/renamed.
  const isDuplicateName = (name, exclude) => {
    const n = (name || '').trim().toLowerCase();
    if (!n) return false;
    return allViews.some(v => v && !v.isSeparator && v.sName
      && v.sName.toLowerCase() === n && v.sName !== exclude);
  };

  // Initialize form when modal opens
  useEffect(() => {
    if (open) {
      if (mode === 'save') {
        // Unified save: default to Update when possible, else Save-as-new.
        // Arrange mode passes initialName (the top-bar name) to prefill the save-as-new field.
        setSaveTarget(canOverwrite ? 'overwrite' : 'new');
        setViewName(canOverwrite ? (currentView.sName || '') : (initialName || ''));
        setIsGlobal(canOverwrite ? (currentView.fGlobal || false) : false);
        setIsDefault(canOverwrite ? (currentView.fDefault || false) : false);
        setIsDividia(canOverwrite ? !!currentView.fDividia : false);
      } else if (mode === 'edit' && currentView) {
        // Pre-fill with existing view properties
        setViewName(currentView.sName || '');
        setIsGlobal(currentView.fGlobal || false);
        setIsDefault(currentView.fDefault || false);
        setIsDividia(!!currentView.fDividia);
      } else if (mode === 'add') {
        // Reset for new view (Arrange may seed the name from the top-bar field)
        setViewName(initialName || '');
        setIsGlobal(false);
        setIsDefault(false);
        setIsDividia(false);
      }
      setNameError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, currentView]);

  const handleConfirm = () => {
    const overwriting = mode === 'save' && saveTarget === 'overwrite';
    const savingAsNew = mode === 'add' || (mode === 'save' && saveTarget === 'new');

    // Name is required except when overwriting the current view (its name is fixed).
    if (!overwriting && (mode === 'add' || mode === 'save' || mode === 'edit') && (!viewName || viewName.trim() === '')) {
      setNameError('View name is required');
      return;
    }

    // Case-insensitive duplicate-name check for new views + renames (never when overwriting).
    if (savingAsNew || (mode === 'edit' && viewName.trim() !== currentView?.sName)) {
      const exclude = mode === 'edit' ? currentView?.sName : undefined;
      if (isDuplicateName(viewName, exclude)) {
        setNameError('A view with this name already exists');
        return;
      }
    }

    // Prepare view data. Force-drop options the role can't use (defense in depth — the
    // backend gate is authoritative, but never send a scope the UI shouldn't have offered).
    const global = canGlobal ? isGlobal : false;
    const viewData = {
      sName: overwriting ? currentView.sName : viewName.trim(),
      fGlobal: global,
      fDefault: canDefaultTarget ? isDefault : false,
      // Dividia-internal is a property of a GLOBAL, and only a bRole=1 master may set it.
      fDividia: (canDividia && global) ? isDividia : false,
      // Unified save: tell the caller to UPDATE the current view vs CREATE a new one.
      overwrite: overwriting
    };

    onConfirm(viewData);
  };

  const getTitle = () => {
    switch (mode) {
      case 'add':
        return 'Add New View';
      case 'save':
        return 'Save View';
      case 'edit':
        return 'Edit View Properties';
      case 'delete':
        return 'Delete View';
      default:
        return 'View Operation';
    }
  };

  const getContent = () => {
    if (mode === 'delete') {
      // Delete confirmation. Entry is already gated (ViewList only shows Delete when the current
      // user may delete THIS view — Smart never; Default/Global admin only; User owner only), so
      // reaching here means the delete is allowed (an admin CAN delete a Default now, #86bb7phhh).
      return (
        <Typography>
          Are you sure you want to delete the view "{currentView?.sName}"?
          <br /><br />
          This action cannot be undone.
        </Typography>
      );
    }

    // Add, Save, or Edit mode
    const hasOtherDefaultView = allViews.some(v =>
      v.fDefault && v.sName !== currentView?.sName
    );
    // Name field shows for everything except overwriting the current view (its name is fixed).
    const showNameField = !(mode === 'save' && saveTarget === 'overwrite');

    return (
      <>
        {mode === 'save' && canOverwrite && (
          <RadioGroup value={saveTarget} onChange={(e) => setSaveTarget(e.target.value)}>
            <FormControlLabel value="overwrite" control={<Radio color="primary" />} label={`Update "${currentView.sName}"`} />
            <FormControlLabel value="new" control={<Radio color="primary" />} label="Save as a new view" />
          </RadioGroup>
        )}
        {mode === 'save' && !canOverwrite && (
          <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 4 }}>
            The current view can't be overwritten — saving as a new view.
          </Typography>
        )}

        {showNameField && (
          <TextField
            autoFocus
            margin="dense"
            label="View Name"
            type="text"
            fullWidth
            value={viewName}
            onChange={(e) => {
              setViewName(e.target.value);
              setNameError('');
            }}
            error={!!nameError}
            helperText={nameError}
          />
        )}
    
        {canGlobal && (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isGlobal}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setIsGlobal(checked);
                    // §6 safe default: when a Dividia master makes a view global, default it
                    // to Dividia-internal so a staff view never accidentally leaks to a
                    // customer; they uncheck below to make it customer-facing.
                    if (checked && canDividia) setIsDividia(true);
                    if (!checked) setIsDividia(false);
                  }}
                  color="primary"
                />
              }
              label="Global (visible to all users)"
              style={{ marginTop: '16px' }}
            />
            <br/>
          </>
        )}
        {canDividia && isGlobal && (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isDividia}
                  onChange={(e) => setIsDividia(e.target.checked)}
                  color="primary"
                />
              }
              label="Dividia internal (staff only — not visible to customers)"
            />
            <br/>
          </>
        )}
        {canGlobal && (
          <FormControlLabel
            control={
              <Checkbox
                checked={isDefault && !crossServer}
                disabled={crossServer}
                onChange={(e) => setIsDefault(e.target.checked)}
                color="primary"
              />
            }
            label="Default view for this server"
          />
        )}

        {canGlobal && crossServer && (
          <Typography variant="caption" color="textSecondary" style={{ marginTop: '8px', display: 'block' }}>
            A default view must be from a single system — this layout spans more than one server.
          </Typography>
        )}

        {canGlobal && hasOtherDefaultView && isDefault && !crossServer && (
          <Typography variant="caption" color="textSecondary" style={{ marginTop: '8px', display: 'block' }}>
            Note: Setting this as default will unmark the current default view.
          </Typography>
        )}

        {mode === 'save' && (
          <Typography variant="caption" color="textSecondary" style={{ marginTop: '16px', display: 'block' }}>
            {saveTarget === 'overwrite'
              ? `Saves the current camera layout into "${currentView.sName}".`
              : 'Saves the current camera layout as a new view.'}
          </Typography>
        )}

        {mode === 'edit' && (
          <Typography variant="caption" color="textSecondary" style={{ marginTop: '16px', display: 'block' }}>
            This will update the view properties without changing the camera slots.
          </Typography>
        )}
      </>
    );
  };

  const getConfirmButtonText = () => {
    switch (mode) {
      case 'add':
        return 'Add';
      case 'save':
        return 'Save';
      case 'edit':
        return 'Update';
      case 'delete':
        return 'Delete';
      default:
        return 'Confirm';
    }
  };

  const isDeleteDisabled = mode === 'delete' && !canDeleteView(currentView, ctx);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{getTitle()}</DialogTitle>

      <DialogContent>
        {getContent()}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="default">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="primary"
          variant="contained"
          disabled={isDeleteDisabled}
        >
          {getConfirmButtonText()}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewModal;
